import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const DEFAULTS = {
  feminine_voice_visible: false,
};

/**
 * useAppSettings — fetches global feature flags from /api/app-settings.
 * Used by the public UI to decide which voice options to expose.
 * Admin components can also update the flags via the returned `update` fn.
 */
export default function useAppSettings() {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const pendingRef = useRef(0);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/app-settings`);
      setSettings({ ...DEFAULTS, ...(r.data || {}) });
    } catch (e) {
      console.warn('[useAppSettings] fetch failed:', e);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const update = useCallback(async (patch) => {
    // Optimistic update: reflect the change in UI immediately so the toggle
    // refreshes even if the network response is slow or the response payload
    // is unexpectedly stripped.
    setSettings((prev) => ({ ...prev, ...patch }));
    pendingRef.current += 1;
    try {
      const r = await axios.put(`${API}/api/app-settings`, patch);
      const data = r.data || {};
      pendingRef.current -= 1;
      // Only merge the server response if no newer update is in flight,
      // otherwise a stale response would overwrite the newer optimistic value.
      if (pendingRef.current === 0) {
        setSettings((prev) => ({ ...prev, ...data }));
      }
      return data;
    } catch (e) {
      pendingRef.current -= 1;
      // Revert the optimistic update on failure
      try {
        const r = await axios.get(`${API}/api/app-settings`);
        if (pendingRef.current === 0) setSettings({ ...DEFAULTS, ...(r.data || {}) });
      } catch { /* keep optimistic value */ }
      throw e;
    }
  }, []);

  return { settings, loaded, refresh, update };
}
